/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { flatten } from 'lodash'
import DOMPurify from 'dompurify'

import { ApiObservation, ApiObservationResponseItem, ObservationStatKey } from '../models/api'
import { ReportType } from '../models/client/constants'
import {
  GateObservation,
  ObservationDraft,
  ObservationKey,
  ObservationMetadata,
  ObservationOutgateData,
} from '../models/client/observation'
import type { AddAssetForm, AssetStats } from '../pages/yard-inventory/types'
import { ApiError } from '../util/errors/ApiError'

import { ApiMetaType, apiRequest } from './apiRequest'
import {
  applyAssetsFilters,
  buildApiResponseAsset,
  buildApiResponseObservation,
  translateAsset,
  translateGateFeedObservation,
} from './tools'
import { GateFeedFilter, ObservationSort } from './type'

const INCLUDED_OBSERVATION_FIELDS =
  '[vehicle.[type,carrier],chassis.[type],load.[type],gate.[site],vehicleOutwardObservation,chassisOutwardObservation,loadOutwardObservation]'

const sanitize = DOMPurify.sanitize

const buildFilters = (params: {
  filters: GateFeedFilter[]
  inventoryOnly?: boolean
}): Record<string, any> => {
  const { filters, inventoryOnly } = params
  const filterParams = filters.reduce((draft, filter): Record<string, any> => {
    switch (filter.type) {
      case 'date':
        if (!filter.value.start || !filter.value.end) return draft
        draft.detectedAt = {
          $between: [filter.value.start, filter.value.end],
        }
        return draft
      case 'site': {
        draft['gate.siteId'] = filter.value.value
        return draft
      }
      case 'gate': {
        draft.gateId = filter.value.value
        return draft
      }
      case 'direction': {
        draft.direction = filter.value
        return draft
      }
      case 'property': {
        filter.value.forEach((propertyFilter) => {
          draft[propertyFilter.name] = {
            $ilike: `${sanitize(propertyFilter.value)}%`,
          }
        })

        if (inventoryOnly) {
          if (filter.category === 'load') {
            draft.loadOutwardObservationId = { $isnull: true }
          } else if (filter.category === 'chassis') {
            draft.chassisOutwardObservationId = { $isnull: true }
          } else if (filter.category === 'vehicle') {
            draft.vehicleOutwardObservationId = { $isnull: true }
          } else {
            draft.loadOutwardObservationId = { $isnull: true }
            draft.vehicleOutwardObservationId = { $isnull: true }
            draft.chassisOutwardObservationId = { $isnull: true }
          }
        }

        return draft
      }
      case 'id':
        draft.id = sanitize(filter.value)
        return draft
      case 'loadType':
        draft['load.typeId'] = filter.value
        return draft
      case 'vehicleType':
        draft['vehicle.typeId'] = filter.value
        return draft
      case 'chassisType':
        draft['chassis.typeId'] = filter.value
        return draft
      default:
        return draft
    }
  }, {} as Record<string, any>)

  return filterParams
}

const buildSort = (direction: 'ASC' | 'DESC', column: ObservationKey): string => {
  const columnsToField = {
    assetTimestamp: 'detectedAt',
    assetDirection: 'direction',
    loadType: 'load.type.name',
    loadNumber: 'load.number',
    hasSeal: 'hasSeal',
    sealNumber: 'sealNumber',
    loadLicensePlate: 'load.licensePlate',
    loadDamage: 'loadDamage',
    loadDwellTimeHr: 'load_dwell_time',
    chassisNumber: 'chassis.number',
    chassisLicensePlate: 'chassis.licensePlate',
    chassisDamage: 'chassisDamage',
    isBare: 'chassisIsBare',
    chassisDwellTimeHr: 'chassis_dwell_time',
    vehicleType: 'vehicle.type.name',
    vehicleCarrierName: 'vehicle.carrier.name',
    vehicleNumber: 'vehicle.number',
    vehicleDotNumber: 'vehicle.dotNumber',
    vehicleLicensePlate: 'vehicle.licensePlate',
    vehicleDamage: 'vehicleDamage',
    vehicleDwellTimeHr: 'detectedAt',
    vehicleWeightClass: 'vehicle.weightClass',
    vehicleFuelType: 'vehicle.fuelType',
  }
  if (!columnsToField[column]) {
    throw new ApiError({
      type: 'BadRequest',
      cause: new Error(`Invalid sort key: ${JSON.stringify(column)}`),
    })
  }
  return `${columnsToField[column]} ${direction}`
}

const getYardReport = async (params: {
  filters: GateFeedFilter[]
  token: string
  page?: number
  size?: number
  includeVehicles?: boolean
  sort?: ObservationSort | null
}): Promise<{
  data: GateObservation[]
  meta: ApiMetaType
}> => {
  const { filters, token, page = 0, size = 10, sort, includeVehicles } = params
  // @ts-ignore
  const sortColumn =
    sort?.column === 'TIME' || sort?.column === 'DATE' ? 'assetTimestamp' : sort?.column
  const sortBy = buildSort(sort?.direction || 'DESC', sortColumn || 'assetTimestamp')

  let filter
  const isBare = filters.some((f) => f.id === 'isBare')

  filter = {
    ...buildFilters({ filters, inventoryOnly: true }),
  }

  const { data: assets, meta } = await apiRequest<ApiObservationResponseItem[]>({
    path: '/observations/yard-report',
    token,
    method: 'POST',
    data: {
      page: {
        number: page,
        size,
      },
      filter,
      sort: sortBy,
      report: true,
      isBare,
      includeVehicles,
    },
  })

  const allAssets: any[] = []

  assets.forEach((asset) => {
    allAssets.push(translateAsset(asset, isBare))
  })

  const flattenedAssets = flatten(allAssets)
  const filteredAssets = applyAssetsFilters(flattenedAssets, filters)

  return {
    data: filteredAssets,
    meta,
  }
}

const getGateFeedReport = async (params: {
  filters: GateFeedFilter[]
  token: string
  page?: number
  size?: number
  includeVehicles?: boolean
  sort?: ObservationSort | null
}): Promise<{
  data: GateObservation[]
  meta: ApiMetaType
}> => {
  const { filters, token, page = 0, size = 10, sort, includeVehicles } = params
  // @ts-ignore
  const sortColumn =
    sort?.column === 'TIME' || sort?.column === 'DATE' ? 'assetTimestamp' : sort?.column
  const sortBy = buildSort(sort?.direction || 'DESC', sortColumn || 'assetTimestamp')

  let filter
  const isBare = filters.some((f) => f.id === 'isBare')

  filter = {
    ...buildFilters({ filters, inventoryOnly: false }),
  }

  const { data: assets, meta } = await apiRequest<ApiObservationResponseItem[]>({
    path: '/observations/gate-feed',
    token,
    method: 'POST',
    data: {
      page: {
        number: page,
        size,
      },
      filter,
      sort: sortBy,
      report: false,
      isBare,
      includeVehicles,
    },
  })

  const allAssets: any[] = []

  assets.forEach((asset) => {
    allAssets.push(translateGateFeedObservation(asset))
  })

  const flattenedAssets = flatten(allAssets)

  return {
    data: flattenedAssets,
    meta,
  }
}

const getYardAsset = async (params: {
  token: string
  observationId: string
}): Promise<GateObservation> => {
  const { data } = await apiRequest<ApiObservationResponseItem>({
    token: params.token,
    path: `/observations/${params.observationId}`,
    include: [INCLUDED_OBSERVATION_FIELDS],
    method: 'GET',
  })
  return data
}

const getYardStats = async (params: {
  filters: GateFeedFilter[]
  token: string
}): Promise<{
  data: AssetStats[]
}> => {
  const { filters, token } = params

  let filter

  filter = {
    ...buildFilters({ filters }),
  }

  const { data } = await apiRequest<AssetStats[]>({
    path: '/observations/yard-stats',
    token,
    method: 'POST',
    data: {
      filter,
    },
  })

  return {
    data,
  }
}

const addObservation = async ({
  token,
  draft,
  meta,
  report,
}: {
  token: string
  draft: AddAssetForm
  meta?: ObservationMetadata
  report?: boolean
}): Promise<void> => {
  let payload: any = {}

  const CUSTOMER_EXCEPTION = '3'
  const observation = buildApiResponseAsset({ draft, meta })

  if (report) {
    // (Inventory) Report: add observation via observations/merge (no-match)
    payload = {
      data: {
        observations: [
          {
            ...observation,
            tags: ['manual'],
            statusCode: CUSTOMER_EXCEPTION,
            detectedAt: observation.detectedAt ?? new Date().toISOString(), // SPEC: fill in wall time unless `detectedAt` is specified - since required (NOTE: also done in UI editor default values)
            direction: true, // ingate

            // SPEC: fill in Vehicle type to be `Tractor` - since required (TODO)
          },
        ],
      },
      options: {
        noMatch: true, // create
      },
    }
  } else {
    // Gate Feed: add observation via observations/merge (no-match)
    payload = {
      data: {
        observations: [
          {
            ...observation,
            tags: ['manual'],
            statusCode: CUSTOMER_EXCEPTION,
            detectedAt: observation.detectedAt ?? new Date().toISOString(), // SPEC: fill in wall time unless `detectedAt` is specified - since required (NOTE: also done in UI editor default values)

            // SPEC: fill in Vehicle type to be `Tractor` - since required (TODO)
          },
        ],
      },
      options: {
        noMatch: true, // create
      },
    }
  }
  payload.data = payload.data.observations

  try {
    const result = await apiRequest<any, any>({
      method: 'POST',
      path: `/observations/merge`,
      token,
      data: payload.data,
      parameters: payload.options,
      include: [INCLUDED_OBSERVATION_FIELDS],
      disableCache: true,
    })

    return result?.data
  } catch (e) {
    throw e
  }
}

const editObservation = async ({
  token,
  observationId,
  draft,
  meta,
  report,
}: {
  token: string
  observationId: string
  draft: AddAssetForm
  meta?: ObservationMetadata
  report?: boolean
}): Promise<void> => {
  let payload: any = {}

  const CUSTOMER_EXCEPTION = '3'
  const observation = buildApiResponseAsset({ draft, meta })

  payload = {
    data: {
      observations: [
        {
          ...observation,
          id: observationId,
          statusCode: CUSTOMER_EXCEPTION,
        },
      ],
    },
    options: {
      noMatch: false, // merge/update
    },
  }

  payload = {
    data: payload?.data?.observations?.[0] || null,
  }

  console.warn('editObservationV2', { report, payload })

  try {
    const result = await apiRequest<ApiObservation, ApiObservationResponseItem>({
      method: 'PUT',
      path: '/observations/merge-update',
      token,
      data: payload.data,
      parameters: payload.options,
      include: [INCLUDED_OBSERVATION_FIELDS],
      disableCache: true,
    })

    return result?.data
  } catch (e) {
    throw e
  }
}

const deleteObservation = async ({
  token,
  observationId,
  draft,
  meta,
  report,
}: {
  token: string
  observationId: string
  draft?: ObservationDraft
  meta?: ObservationMetadata
  report?: boolean
}): Promise<void> => {
  let payload: any = {}

  const CUSTOMER_EXCEPTION = '3'
  const observation = buildApiResponseObservation({ draft, meta })

  // (Inventory) Report: add outward observation via observations/merge (no-match)
  payload = {
    data: {
      ...observation,
      id: observationId,
      statusCode: CUSTOMER_EXCEPTION,
      direction: false, // SPEC: create outgate observation
      archivedAt: new Date().toISOString(), // SPEC: delete = archive
    },
    options: {
      noMatch: true, // create
    },
  }

  console.warn('deleteObservationV2', { payload })

  try {
    const result = await apiRequest<any, any>({
      method: 'PUT',
      path: '/observations/merge-update',
      token,
      data: payload.data,
      parameters: payload.options,
      include: [INCLUDED_OBSERVATION_FIELDS],
      disableCache: true,
    })
    const data = result?.data
    return data
  } catch (e) {
    throw e
  }
}

const outgateObservation = async ({
  token,
  observationId,
  observation,
  outgateDateTime,
}: {
  token: string
  observationId: string
  observation?: ObservationOutgateData
  outgateDateTime: string
}): Promise<void> => {
  let payload: any = {}

  payload = {
    observation: {
      id: observationId,
      detectedAt: observation?.detectedAt,
      gateId: observation?.gateId,
      vehicleId: observation?.vehicleId || null,
      loadId: observation?.loadId || null,
      chassisId: observation?.chassisId || null,
    },
    outgateSource: 'YMS',
    outgateTimestamp: outgateDateTime,
  }

  console.warn('outgating observation', payload)

  try {
    const result = await apiRequest<any, any>({
      method: 'POST',
      path: `/observations/outgate`,
      token,
      data: { ...payload },
    })

    return result?.data
  } catch (e) {
    throw e
  }
}

export type ObservationStats = {
  key: ObservationStatKey
  label: string
  count: number
}

export type CancelDownloadFn = () => void

export const assetClient = {
  getYardReport,
  getGateFeedReport,
  getYardAsset,
  getYardStats,
  addObservation,
  editObservation,
  deleteObservation,
  outgateObservation,
}
